import { useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import * as Sentry from '@sentry/browser';

import {
    AnswerCallPayload,
    CALL_VIEW_MODES,
    CallStateChangedPayload,
    CallType,
    CallViewChangedPayload,
    ChatAuthTokenPayload,
    ChatInitPayload,
    ChatStudyChangedPayload,
    HangupCallPayload,
    RejectCallPayload,
    SetCallViewPayload,
} from './types';

type IncomingMessage =
    | { type: 'CHAT_AUTH_TOKEN'; payload: ChatAuthTokenPayload }
    | { type: 'CHAT_INIT'; payload: ChatInitPayload }
    | { type: 'CHAT_STUDY_CHANGED'; payload: ChatStudyChangedPayload }
    | { type: 'CHAT_ANSWER_CALL'; payload: AnswerCallPayload }
    | { type: 'CHAT_REJECT_CALL'; payload: RejectCallPayload }
    | { type: 'CHAT_HANGUP_CALL'; payload: HangupCallPayload }
    | { type: 'CHAT_SET_CALL_VIEW'; payload: SetCallViewPayload };

type OutgoingMessage =
    | { type: 'CHAT_FRAME_READY' }
    | { type: 'CHAT_READY' }
    | { type: 'CHAT_ERROR'; payload: { message: string; code?: string } }
    | { type: 'CHAT_UNREAD_COUNT'; payload: { count: number } }
    | {
          type: 'CHAT_INCOMING_CALL';
          payload: { callId: string; callerName: string; callType: CallType };
      }
    | { type: 'CHAT_INCOMING_CALL_ENDED'; payload: { callId: string } }
    | { type: 'CHAT_CALL_STATE_CHANGED'; payload: CallStateChangedPayload }
    | { type: 'CHAT_CALL_VIEW_CHANGED'; payload: CallViewChangedPayload };

interface IncomingHandlers {
    onAuthToken: (payload: ChatAuthTokenPayload) => void;
    onInit: (payload: ChatInitPayload) => void;
    onStudyChanged: (payload: ChatStudyChangedPayload) => void;
    onAnswerCall: (payload: AnswerCallPayload) => void;
    onRejectCall: (payload: RejectCallPayload) => void;
    onHangupCall: (payload: HangupCallPayload) => void;
    onSetCallView: (payload: SetCallViewPayload) => void;
}

function isAuthTokenPayload(p: unknown): p is ChatAuthTokenPayload {
    if (!p || typeof p !== 'object') return false;
    const x = p as Record<string, unknown>;
    return (
        typeof x.accessToken === 'string' &&
        x.accessToken.length > 0 &&
        typeof x.refreshToken === 'string' &&
        x.refreshToken.length > 0 &&
        (x.expiresIn === undefined || typeof x.expiresIn === 'number')
    );
}

function isInitPayload(p: unknown): p is ChatInitPayload {
    if (!p || typeof p !== 'object') return false;
    const x = p as Record<string, unknown>;
    return (
        typeof x.studyId === 'number' &&
        x.studies !== null &&
        typeof x.studies === 'object' &&
        (x.locale === undefined || typeof x.locale === 'string')
    );
}

function isStudyChangedPayload(p: unknown): p is ChatStudyChangedPayload {
    if (!p || typeof p !== 'object') return false;
    const x = p as Record<string, unknown>;
    return typeof x.studyId === 'number' && x.studies !== null && typeof x.studies === 'object';
}

function isAnswerCallPayload(p: unknown): p is AnswerCallPayload {
    if (!p || typeof p !== 'object') return false;
    const x = p as Record<string, unknown>;
    return typeof x.callId === 'string' && x.callId.length > 0 && typeof x.withVideo === 'boolean';
}

function isRejectCallPayload(p: unknown): p is RejectCallPayload {
    if (!p || typeof p !== 'object') return false;
    const x = p as Record<string, unknown>;
    return typeof x.callId === 'string' && x.callId.length > 0;
}

function isHangupCallPayload(p: unknown): p is HangupCallPayload {
    if (!p || typeof p !== 'object') return false;
    const x = p as Record<string, unknown>;
    return typeof x.callId === 'string' && x.callId.length > 0;
}

function isSetCallViewPayload(p: unknown): p is SetCallViewPayload {
    if (!p || typeof p !== 'object') return false;
    const x = p as Record<string, unknown>;
    return x.mode === CALL_VIEW_MODES.FULLSCREEN || x.mode === CALL_VIEW_MODES.MINIMIZED;
}

function parseIncomingMessage(data: unknown): IncomingMessage | null {
    if (!data || typeof data !== 'object') return null;
    const { type, payload } = data as { type?: unknown; payload?: unknown };
    switch (type) {
        case 'CHAT_AUTH_TOKEN':
            return isAuthTokenPayload(payload) ? { type, payload } : null;
        case 'CHAT_INIT':
            return isInitPayload(payload) ? { type, payload } : null;
        case 'CHAT_STUDY_CHANGED':
            return isStudyChangedPayload(payload) ? { type, payload } : null;
        case 'CHAT_ANSWER_CALL':
            return isAnswerCallPayload(payload) ? { type, payload } : null;
        case 'CHAT_REJECT_CALL':
            return isRejectCallPayload(payload) ? { type, payload } : null;
        case 'CHAT_HANGUP_CALL':
            return isHangupCallPayload(payload) ? { type, payload } : null;
        case 'CHAT_SET_CALL_VIEW':
            return isSetCallViewPayload(payload) ? { type, payload } : null;
        default:
            return null;
    }
}

export function usePostMessageBridge(allowedOrigins: Array<string>, handlers: IncomingHandlers) {
    const parentOriginRef = useRef<string | null>(null);
    const handlersRef = useRef(handlers);

    useLayoutEffect(() => {
        handlersRef.current = handlers;
    });

    const sendToParent = useCallback(
        (message: OutgoingMessage) => {
            if (!window.parent || window.parent === window) return;

            const postSafely = (origin: string) => {
                try {
                    window.parent.postMessage(message, origin);
                } catch (error) {
                    Sentry.withScope((scope) => {
                        scope.setTag('catch_in', 'usePostMessageBridge.sendToParent');
                        scope.setExtras({ origin, messageType: (message as { type?: string }).type });
                        Sentry.captureException(error);
                    });
                }
            };

            const lockedOrigin = parentOriginRef.current;
            if (lockedOrigin !== null) {
                postSafely(lockedOrigin);
                return;
            }
            // Initial handshake: origin not yet locked. Post to each allowed origin;
            // the browser only delivers to a matching parent.
            allowedOrigins.forEach(postSafely);
        },
        [allowedOrigins]
    );

    useEffect(() => {
        const handleMessage = (event: MessageEvent) => {
            if (!allowedOrigins.includes(event.origin)) {
                return;
            }

            const message = parseIncomingMessage(event.data);
            if (message === null) {
                const rawType =
                    event.data && typeof event.data === 'object' && typeof (event.data as { type?: unknown }).type === 'string'
                        ? (event.data as { type: string }).type
                        : '<unknown>';
                sendToParent({
                    type: 'CHAT_ERROR',
                    payload: { message: `Invalid or unknown incoming message: ${rawType}`, code: 'INVALID_PAYLOAD' },
                });
                return;
            }

            if (parentOriginRef.current === null) {
                parentOriginRef.current = event.origin;
            }

            switch (message.type) {
                case 'CHAT_AUTH_TOKEN':
                    handlersRef.current.onAuthToken(message.payload);
                    break;
                case 'CHAT_INIT':
                    handlersRef.current.onInit(message.payload);
                    break;
                case 'CHAT_STUDY_CHANGED':
                    handlersRef.current.onStudyChanged(message.payload);
                    break;
                case 'CHAT_ANSWER_CALL':
                    handlersRef.current.onAnswerCall(message.payload);
                    break;
                case 'CHAT_REJECT_CALL':
                    handlersRef.current.onRejectCall(message.payload);
                    break;
                case 'CHAT_HANGUP_CALL':
                    handlersRef.current.onHangupCall(message.payload);
                    break;
                case 'CHAT_SET_CALL_VIEW':
                    handlersRef.current.onSetCallView(message.payload);
                    break;
                default: {
                    const exhaustive: never = message;
                    void exhaustive;
                }
            }
        };

        window.addEventListener('message', handleMessage);
        return () => window.removeEventListener('message', handleMessage);
    }, [allowedOrigins, sendToParent]);

    return { sendToParent };
}
